import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, colors } from '@insurancemenu/imenu-components-lib';
import { Cart } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useQueryParams } from 'global/hooks/useQuery';
import { useRootStore } from 'store/rootStore';
import FormsSpinner from 'components/common/general/FormSpinner';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import ContinuousRatesPullingModal from 'components/common/modals/ContinuousRatesPullingModal';
import HeaderWithBcBlock from 'components/common/headers/HeaderWithBcBlock';
import PopularPlanCard from '../PopularPlanCard';
import PopularPlansActionBar from './ActionBar';
import NoPlansSection from './NoPlansSection';
import { CustomDisclaimerStyled, ListWrapperStyled, PlansListStyled, CartButtonStyled, createStyles } from './styles';

const PopularPlansContent: React.FC = observer(() => {
  const history = useHistory();

  const query = useQueryParams();
  const cartId = query.get('cartId') || null;
  const pLineId = query.get('productLineId') || null;

  const {
    popularPlansStore: {
      loading,
      productLines,
      cart,
      currentProductLineId,
      currentEffectiveDate,
      currentZipCode,
      allPortfolios,
      continuousPullingRatesModalOpened,
      calculateRatesAttempts,
      collectInitialData,
      setContinuousPullingRatesModal,
    },
    domainStore: { cartItemsQuantity, mostPopularDisclaimer },
  } = useRootStore();

  const convertedCart = toJS(cart);
  const convertedPLines = productLines.map((pLine) => toJS(pLine)).slice();
  const convertedPortfolios = allPortfolios.map((portfolio) => toJS(portfolio)).slice();

  const portfoliosQuantity = convertedPortfolios.length;

  // TODO remove when HeaderWithBcBlock will be updated
  const classes = createStyles();

  useEffect(() => {
    if (cartId) {
      void collectInitialData(cartId, pLineId ? (pLineId as ProductLineId) : convertedPLines[0].productLineId);
    }
  }, []);

  const handleBrowsingRedirect = (): void => {
    history.push(
      `/browsing?groupId=${convertedCart.groupId}&cartId=${cartId}&productLineId=${currentProductLineId}&cartType=browsing&backType=popular`,
    );
  };

  const handleCartRedirect = (): void => {
    history.push(`/cart?groupId=${convertedCart.groupId}&cartId=${cartId}&cartType=popular`);
  };

  const handleRateSegmentsRedirect = (): void => {
    history.push(`/rate-segments?groupId=${convertedCart.groupId}&cartId=${cartId}&cartType=rate-segment`);
  };

  return (
    <>
      <HeaderWithBcBlock
        title="Our Most Popular Plans"
        bcBlockText={`Effective date: ${
          currentEffectiveDate ? normDateToShortAmericanDate(currentEffectiveDate) : ''
        }, Zip: ${currentZipCode}`}
        headerClassName={classes.header}
        onRedirect={handleRateSegmentsRedirect}
      />
      <PopularPlansActionBar />

      {!loading && mostPopularDisclaimer && (
        <CustomDisclaimerStyled dangerouslySetInnerHTML={{ __html: mostPopularDisclaimer }} />
      )}
      {!loading && portfoliosQuantity === 0 && <NoPlansSection onBrowseClick={handleBrowsingRedirect} />}
      <ListWrapperStyled $isListEmpty={portfoliosQuantity === 0}>
        {loading ? (
          <FormsSpinner display={loading} semiTransparentBg={false} />
        ) : (
          <>
            <PlansListStyled>
              {convertedPortfolios
                .sort((a, b) => (a.eStoreTagId > b.eStoreTagId ? 1 : -1))
                .map((portfolio, index) => {
                  return (
                    <PopularPlanCard
                      portfolio={portfolio}
                      key={portfolio.id}
                      planIndex={index}
                      advantages={portfolio.advantages}
                      portfoliosQuantity={portfoliosQuantity}
                    />
                  );
                })}
            </PlansListStyled>

            {cartItemsQuantity > 0 && (
              <CartButtonStyled
                as={Button}
                color="custom"
                customColor={colors.orange[600]}
                leftIconProp={<Cart />}
                size="large"
                onClick={handleCartRedirect}
              >
                Go to cart
              </CartButtonStyled>
            )}
          </>
        )}
      </ListWrapperStyled>
      <ContinuousRatesPullingModal
        show={continuousPullingRatesModalOpened}
        onClose={(): void => setContinuousPullingRatesModal(false)}
        attemptNumber={calculateRatesAttempts}
      />
    </>
  );
});

export default PopularPlansContent;
